import React, { useState } from "react";
import { Box, Tabs, Tab } from "@mui/material";

// Import tab content components
import { OrderDetailInfo } from "./orderDetails";
import OrderHistory from "./OrderHistory";
import { DocumentManagement } from "../../documents/components";
import ToolbarDetailOrder from "./ToolbarDetailOrder";
import TaskManagement from "@/modules/tasks/components/TaskManagement";
import OrderIncomeExpenses from "./OrderIncomeExpenses";
import OrderMaterials from "./orderDetails/OrderMaterials";
import { OrderProgress } from '@/modules/order-progress';
import { usePermissions } from "@/__core/permissions";
import { RelatedEntityTabRecord } from "@/__core/tabs";
import { MaterialLine } from "./orderDetails/OrderMaterials";

interface OrderDetailTabsProps {
  formData: any;
  customers: any[];
  companies: any[];
  salesChannels: any[];
  wallets?: any[];
  orderStatuses: any[];
  employees: any[];
  orderItems: any[];
  units: any[];
  productTypes?: any[];
  additionalFees: any[];
  totalAmount: number;
  grandTotal: number;
  isEditMode: boolean;
  orderId?: string;
  contextCompanyId?: string;
  disabled?: boolean;
  canViewOrderSummary?: boolean;
  onInputChange: (field: string, value: any) => void;
  onItemChange: (index: number, field: string, value: any) => void;
  onAddOrderItem: () => void;
  onAddGroupItem: () => void;
  onRemoveOrderItem: (index: number) => void;
  onReorderItems: (items: any[]) => void;
  onOrderStatusCreated: (status: any) => void;
  onOrderStatusUpdated?: () => Promise<void>;
  onSalesChannelCreated: (channel: any) => void;
  onWalletCreated?: (wallet: any) => void;
  onAdditionalFeesChange: (fees: any[]) => void;
  onPaymentCollected?: (transaction: any) => void;
  onPrint?: (type: string) => void;
  onDelete?: () => void;
  onExport?: () => void;
  onDateChangesChange?: (hasChanges: boolean) => void;
  onSaveDatesRef?: (saveFn: (() => Promise<void>) | null) => void;
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  canPrint?: boolean;
  canExport?: boolean;
  canDelete?: boolean;
  onOpenIssue?: (record: RelatedEntityTabRecord<MaterialLine>) => void;
}

const OrderDetailTabs: React.FC<OrderDetailTabsProps> = ({
  formData,
  customers,
  companies,
  salesChannels,
  wallets = [],
  orderStatuses,
  employees,
  orderItems,
  units,
  productTypes = [],
  additionalFees,
  totalAmount,
  grandTotal,
  isEditMode,
  orderId,
  contextCompanyId,
  disabled = false,
  canViewOrderSummary = true,
  onInputChange,
  onItemChange,
  onAddOrderItem,
  onAddGroupItem,
  onRemoveOrderItem,
  onReorderItems,
  onOrderStatusCreated,
  onOrderStatusUpdated,
  onSalesChannelCreated,
  onWalletCreated,
  onAdditionalFeesChange,
  onPaymentCollected,
  onPrint,
  onDelete,
  onExport,
  onDateChangesChange,
  onSaveDatesRef,
  activeTab: activeTabProp,
  onActiveTabChange,
  canPrint = false,
  canExport = false,
  canDelete = false,
  onOpenIssue,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState("info");
  const activeTab = activeTabProp ?? internalActiveTab;
  const setActiveTab = React.useCallback((tab: string) => {
    if (activeTabProp === undefined) {
      setInternalActiveTab(tab);
    }
    onActiveTabChange?.(tab);
  }, [activeTabProp, onActiveTabChange]);

  const [mountedTabs, setMountedTabs] = useState<Set<string>>(() => new Set(["info"]));
  const { hasPermission } = usePermissions();

  const canViewHistory = hasPermission('orders.historicalorder', 'read');
  const canViewDocuments = hasPermission('documents', 'read');
  const canViewTasks = hasPermission('tasks.task', 'read') || hasPermission('tasks', 'read');
  const canViewCosts =
    hasPermission('transaction', 'read') ||
    hasPermission('transaction', 'view_all_transactions') ||
    hasPermission('transaction', 'view_income_only') ||
    hasPermission('transaction', 'view_expense_only');
  const canViewProgress = hasPermission('orders.orderprogress', 'read');
  const canViewMaterials = hasPermission('inventory.issue', 'read');

  const visibleTabs = React.useMemo(() => {
    return [
      { label: 'Thông tin chi tiết', value: 'info', visible: true, disabled: false },
      { label: 'Lịch sử', value: 'history', visible: canViewHistory, disabled: !isEditMode },
      { label: 'Tài liệu', value: 'documents', visible: canViewDocuments, disabled: !isEditMode },
      { label: 'Nhiệm vụ', value: 'tasks', visible: canViewTasks, disabled: !isEditMode },
      { label: 'Thu chi', value: 'costs', visible: canViewCosts, disabled: !isEditMode },
      { label: 'Vật tư', value: 'materials', visible: canViewMaterials, disabled: !isEditMode },
      { label: 'Tiến độ', value: 'progress', visible: canViewProgress, disabled: !isEditMode },
    ].filter((tab) => tab.visible);
  }, [canViewCosts, canViewDocuments, canViewHistory, canViewMaterials, canViewProgress, canViewTasks, isEditMode]);

  React.useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(visibleTabs[0]?.value || 'info');
    }
  }, [activeTab, visibleTabs, setActiveTab]);

  React.useEffect(() => {
    if (!isEditMode && activeTab !== 'info') {
      setActiveTab('info');
    }
  }, [isEditMode, activeTab, setActiveTab]);

  React.useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) {
        return prev;
      }
      return new Set(prev).add(activeTab);
    });
  }, [activeTab]);

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const tabPanelSx = (tabValue: string) => ({
    display: activeTab === tabValue ? 'block' : 'none',
    width: '100%',
    minWidth: 0,
  });

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
      {/* Tab Navigation and Toolbar */}
      <Box sx={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        minWidth: 0,
        borderBottom: 1, 
        borderColor: "divider", 
        mb: 2 
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="order detail tabs"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
            },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} disabled={tab.disabled} />
          ))}
        </Tabs>
        
        {isEditMode && (
          <ToolbarDetailOrder
            onPrint={onPrint}
            onDelete={onDelete}
            onExport={onExport}
            canPrint={canPrint}
            canExport={canExport}
            canDelete={canDelete}
          />
        )}
      </Box>

      {/* Tab panels stay mounted when switching tabs so local form/UI state is preserved */}
      <Box
        role="tabpanel"
        aria-hidden={activeTab !== 'info'}
        sx={tabPanelSx('info')}
      >
        <OrderDetailInfo
          formData={formData}
          customers={customers}
          companies={companies}
          salesChannels={salesChannels}
          wallets={wallets}
          orderStatuses={orderStatuses}
          employees={employees}
          orderItems={orderItems}
          units={units}
          additionalFees={additionalFees}
          totalAmount={totalAmount}
          grandTotal={grandTotal}
          isEditMode={isEditMode}
          orderId={orderId}
          contextCompanyId={contextCompanyId}
          disabled={disabled}
          canViewOrderSummary={canViewOrderSummary}
          onInputChange={onInputChange}
          onItemChange={onItemChange}
          onAddOrderItem={onAddOrderItem}
          onAddGroupItem={onAddGroupItem}
          onRemoveOrderItem={onRemoveOrderItem}
          onReorderItems={onReorderItems}
          productTypes={productTypes}
          onOrderStatusCreated={onOrderStatusCreated}
          onOrderStatusUpdated={onOrderStatusUpdated}
          onSalesChannelCreated={onSalesChannelCreated}
          onWalletCreated={onWalletCreated}
          onAdditionalFeesChange={onAdditionalFeesChange}
          onPaymentCollected={onPaymentCollected}
        />
      </Box>

      {canViewHistory && mountedTabs.has('history') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'history'} sx={tabPanelSx('history')}>
          <OrderHistory orderId={orderId} additionalFees={additionalFees} />
        </Box>
      )}

      {canViewDocuments && mountedTabs.has('documents') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'documents'} sx={tabPanelSx('documents')}>
          <DocumentManagement orderId={orderId} />
        </Box>
      )}

      {canViewTasks && mountedTabs.has('tasks') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'tasks'} sx={tabPanelSx('tasks')}>
          <TaskManagement orderId={orderId} contextCompanyId={formData?.company_id || contextCompanyId} />
        </Box>
      )}

      {canViewCosts && mountedTabs.has('costs') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'costs'} sx={tabPanelSx('costs')}>
          <OrderIncomeExpenses orderId={orderId} contextCompanyId={contextCompanyId} />
        </Box>
      )}

      {canViewMaterials && mountedTabs.has('materials') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'materials'} sx={tabPanelSx('materials')}>
          <OrderMaterials
            orderId={orderId}
            salesAmount={grandTotal}
            active={activeTab === 'materials'}
            companyId={formData?.company_id || contextCompanyId}
            onOpenIssue={onOpenIssue}
          />
        </Box>
      )}
      {canViewProgress && mountedTabs.has('progress') && (
        <Box role="tabpanel" aria-hidden={activeTab !== 'progress'} sx={tabPanelSx('progress')}>
          <OrderProgress
            orderId={orderId}
            orderItems={orderItems}
            productTypes={productTypes}
            statusId={formData?.status_id}
            orderStatuses={orderStatuses}
            disabled={disabled}
            onDateChangesChange={onDateChangesChange}
            onSaveDatesRef={onSaveDatesRef}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrderDetailTabs;
